import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info, Minus, Pause, Play, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  SIX_MST_REFERENCE_METADATA,
  buildSixMSTPayload,
  formatStepStopReasonLabel,
  getSixMSTNormativeBand,
  getSixMSTReferenceEquation,
  normalizeSixMSTPayload,
  resolveSixMSTInterpretation,
  summarizeSixMSTResult,
  type SixMSTMinuteReading,
  type SixMSTResultPayload,
  type StepTestStopReason,
  type StepTestTimerBlock,
  type StepTestVitalsBlock,
} from '../step-tests/stepTestsDomain';

type PatientContextInput = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

interface MST6TestProps {
  initial?: SixMSTResultPayload | null;
  isEditing?: boolean;
  patientContext?: PatientContextInput;
  onCancel?: () => void;
  onNext: (payload: SixMSTResultPayload) => void;
}

type VitalsFormState = {
  hr: string;
  spo2: string;
  bpSys: string;
  bpDia: string;
  borgDyspnea: number | null;
  borgLegFatigue: number | null;
};

type MinuteFormState = {
  minute: number;
  hr: string;
  spo2: string;
  totalSteps: string;
  notes: string;
};

type PauseIntervalFormState = {
  start: number;
  end?: number;
  reason?: StepTestStopReason | null;
};

export type { SixMSTResultPayload };

const BORG_VALUES: number[] = [0, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const PAUSE_REASON_OPTIONS: Array<{ value: StepTestStopReason; label: string }> = [
  { value: 'patient_request', label: 'Pedido do paciente' },
  { value: 'symptom_limitation', label: 'Limitação por sintomas' },
  { value: 'safety_stop', label: 'Critério de segurança' },
  { value: 'supervisor_interruption', label: 'Interrupção do avaliador' },
];

const buildVitalsState = (value?: StepTestVitalsBlock | null): VitalsFormState => ({
  hr: value?.hr ?? '',
  spo2: value?.spo2 ?? '',
  bpSys: value?.bp.sys ?? '',
  bpDia: value?.bp.dia ?? '',
  borgDyspnea: value?.borg.dyspnea ?? null,
  borgLegFatigue: value?.borg.legFatigue ?? null,
});

const buildMinuteState = (value?: SixMSTMinuteReading | null, minute = 1): MinuteFormState => ({
  minute,
  hr: value?.hr ?? '',
  spo2: value?.spo2 ?? '',
  totalSteps: value?.totalSteps != null ? String(value.totalSteps) : '',
  notes: value?.notes ?? '',
});

const buildDefaultMinutes = (initial?: SixMSTResultPayload | null): MinuteFormState[] =>
  Array.from({ length: 6 }, (_, index) => buildMinuteState(initial?.perMinute?.[index], index + 1));

const parseNullableNumber = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseInteger = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatClock = (seconds: number): string => {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const clampSeconds = (seconds: number, maxSeconds: number): number =>
  Math.min(Math.max(0, Math.round(maxSeconds)), Math.max(0, Math.round(seconds)));

const toVitalsPayload = (state: VitalsFormState): StepTestVitalsBlock => ({
  hr: state.hr.trim() || null,
  spo2: state.spo2.trim() || null,
  bp: {
    sys: state.bpSys.trim() || null,
    dia: state.bpDia.trim() || null,
  },
  borg: {
    dyspnea: state.borgDyspnea,
    legFatigue: state.borgLegFatigue,
  },
});

const toPauseReport = (intervals: PauseIntervalFormState[]) => {
  const completedIntervals = intervals
    .filter((entry) => typeof entry.end === 'number' && entry.end >= entry.start)
    .map((entry) => {
      const duration = Math.max(0, (entry.end ?? entry.start) - entry.start);
      return {
        from_s: entry.start,
        to_s: entry.end ?? entry.start,
        duration_s: duration,
        from_fmt: formatClock(entry.start),
        to_fmt: formatClock(entry.end ?? entry.start),
        duration_fmt: formatClock(duration),
        reason: entry.reason ?? null,
        reason_label: entry.reason ? formatStepStopReasonLabel(entry.reason) : null,
      };
    });
  const totalStopped = completedIntervals.reduce((sum, entry) => sum + entry.duration_s, 0);
  return {
    total_stopped_s: totalStopped,
    total_stopped_fmt: formatClock(totalStopped),
    intervals: completedIntervals,
  };
};

const MetricCard = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

function BorgSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <label className="space-y-1">
      <div className="text-xs font-medium text-slate-600">{label}</div>
      <select
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        value={value == null ? '' : String(value)}
        onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      >
        <option value="">Não informado</option>
        {BORG_VALUES.map((item) => (
          <option key={`${label}-${item}`} value={item}>
            {String(item).replace('.', ',')}
          </option>
        ))}
      </select>
    </label>
  );
}

function VitalsEditor({
  title,
  state,
  onChange,
}: {
  title: string;
  state: VitalsFormState;
  onChange: (patch: Partial<VitalsFormState>) => void;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 text-xs font-black uppercase tracking-[0.24em] text-slate-400">{title}</div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">FC (bpm)</div>
          <input
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={state.hr}
            onChange={(event) => onChange({ hr: event.target.value })}
          />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">SpO2 (%)</div>
          <input
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={state.spo2}
            onChange={(event) => onChange({ spo2: event.target.value })}
          />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">PAS (mmHg)</div>
          <input
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={state.bpSys}
            onChange={(event) => onChange({ bpSys: event.target.value })}
          />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">PAD (mmHg)</div>
          <input
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={state.bpDia}
            onChange={(event) => onChange({ bpDia: event.target.value })}
          />
        </label>
        <BorgSelect label="Borg dispneia" value={state.borgDyspnea} onChange={(value) => onChange({ borgDyspnea: value })} />
        <BorgSelect label="Borg fadiga de MMII" value={state.borgLegFatigue} onChange={(value) => onChange({ borgLegFatigue: value })} />
      </div>
    </div>
  );
}

function ClockEditor({
  id,
  label,
  value,
  maxSeconds,
  disabled = false,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  maxSeconds: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  const safeValue = clampSeconds(value, maxSeconds);
  const minutes = Math.floor(safeValue / 60);
  const seconds = safeValue % 60;

  const updatePart = (part: 'minutes' | 'seconds', rawValue: string) => {
    const parsed = Number.parseInt(rawValue, 10);
    if (!Number.isFinite(parsed)) return;
    const nextMinutes = part === 'minutes' ? Math.max(0, parsed) : minutes;
    const nextSeconds = part === 'seconds' ? Math.min(59, Math.max(0, parsed)) : seconds;
    onChange(clampSeconds(nextMinutes * 60 + nextSeconds, maxSeconds));
  };

  return (
    <fieldset className="space-y-1" disabled={disabled}>
      <legend className="text-xs font-medium text-slate-600">{label}</legend>
      <div className="flex items-center gap-1.5">
        <label className="sr-only" htmlFor={`${id}-minutes`}>{label} — minutos</label>
        <input
          id={`${id}-minutes`}
          type="number"
          min={0}
          max={Math.floor(maxSeconds / 60)}
          className="w-16 rounded-xl border border-slate-200 bg-white px-2 py-2 text-center text-sm disabled:bg-slate-100"
          value={minutes}
          onChange={(event) => updatePart('minutes', event.target.value)}
        />
        <span className="font-bold text-slate-400">:</span>
        <label className="sr-only" htmlFor={`${id}-seconds`}>{label} — segundos</label>
        <input
          id={`${id}-seconds`}
          type="number"
          min={0}
          max={59}
          className="w-16 rounded-xl border border-slate-200 bg-white px-2 py-2 text-center text-sm disabled:bg-slate-100"
          value={seconds}
          onChange={(event) => updatePart('seconds', event.target.value)}
        />
        <span className="text-xs text-slate-400">min : seg</span>
      </div>
    </fieldset>
  );
}

export default function MST6Test({ initial = null, isEditing = false, patientContext, onCancel, onNext }: MST6TestProps) {
  const normalizedInitial = normalizeSixMSTPayload(initial);
  const [baseline, setBaseline] = useState<VitalsFormState>(buildVitalsState(normalizedInitial?.baseline));
  const [finalBlock, setFinalBlock] = useState<VitalsFormState>(buildVitalsState(normalizedInitial?.final));
  const [minutes, setMinutes] = useState<MinuteFormState[]>(buildDefaultMinutes(normalizedInitial));
  const [notes, setNotes] = useState(normalizedInitial?.notes ?? '');
  const [stepHeightCm, setStepHeightCm] = useState(String(normalizedInitial?.protocol.stepHeightCm ?? 20));
  const [totalSteps, setTotalSteps] = useState<number>(normalizedInitial?.totalSteps ?? 0);
  const [pauseReason, setPauseReason] = useState<StepTestStopReason>('patient_request');
  const [pauseIntervals, setPauseIntervals] = useState<PauseIntervalFormState[]>(
    normalizedInitial?.stop_report.intervals.map((entry) => ({
      start: entry.from_s,
      end: entry.to_s,
      reason: entry.reason,
    })) ?? [],
  );

  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(normalizedInitial?.timer.elapsed_s ?? 0);
  const [startedAt, setStartedAt] = useState<string | null>(normalizedInitial?.timer.startedAt ?? null);
  const [endedAt, setEndedAt] = useState<string | null>(normalizedInitial?.timer.endedAt ?? null);

  const timerStartedAtRef = useRef<number | null>(null);
  const accumulatedSecondsRef = useRef<number>(normalizedInitial?.timer.elapsed_s ?? 0);
  const timerIntervalRef = useRef<number | null>(null);

  const effectivePatientContext = useMemo(
    () => ({
      ...patientContext,
      ageYears: typeof patientContext?.ageYears === 'number' ? patientContext.ageYears : null,
      weightKg: typeof patientContext?.weightKg === 'number' ? patientContext.weightKg : null,
      heightCm: typeof patientContext?.heightCm === 'number' ? patientContext.heightCm : null,
    }),
    [patientContext],
  );

  useEffect(() => {
    if (!timerRunning) {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = window.setInterval(() => {
      const started = timerStartedAtRef.current;
      if (started == null) return;
      const nextElapsed = accumulatedSecondsRef.current + Math.floor((Date.now() - started) / 1000);
      if (nextElapsed >= 360) {
        accumulatedSecondsRef.current = 360;
        setElapsedSeconds(360);
        timerStartedAtRef.current = null;
        setTimerRunning(false);
        setEndedAt(new Date().toISOString());
        return;
      }
      setElapsedSeconds(nextElapsed);
    }, 250);

    return () => {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [timerRunning]);

  const pauseReport = useMemo(() => toPauseReport(pauseIntervals), [pauseIntervals]);

  const payloadPreview = useMemo(() => {
    const minutePayload: SixMSTMinuteReading[] = minutes.map((entry) => ({
      minute: entry.minute,
      hr: entry.hr.trim() || null,
      spo2: entry.spo2.trim() || null,
      totalSteps: parseInteger(entry.totalSteps),
      notes: entry.notes.trim() || null,
    }));
    const timer: StepTestTimerBlock = {
      elapsed_s: elapsedSeconds,
      completed: elapsedSeconds >= 360,
      startedAt,
      endedAt,
    };
    const payload = buildSixMSTPayload({
      protocol: {
        stepHeightCm: parseNullableNumber(stepHeightCm) ?? 20,
      },
      baseline: toVitalsPayload(baseline),
      perMinute: minutePayload,
      final: toVitalsPayload(finalBlock),
      totalSteps,
      stopReport: pauseReport,
      timer,
      notes,
      patientContext: effectivePatientContext,
      finishedAt: endedAt ?? new Date().toISOString(),
    });
    payload.report = {
      ...(payload.report ?? {}),
      interpretation: resolveSixMSTInterpretation(payload),
      referenceSource: SIX_MST_REFERENCE_METADATA.source,
    };
    return payload;
  }, [baseline, elapsedSeconds, effectivePatientContext, endedAt, finalBlock, minutes, notes, pauseReport, startedAt, stepHeightCm, totalSteps]);

  const summary = useMemo(() => summarizeSixMSTResult(payloadPreview), [payloadPreview]);
  const normativeBand = useMemo(() => getSixMSTNormativeBand(effectivePatientContext), [effectivePatientContext]);
  const predictedSteps = useMemo(() => getSixMSTReferenceEquation(effectivePatientContext), [effectivePatientContext]);
  const activePause = pauseIntervals.find((entry) => entry.end == null);
  const pauseTimelineLimit = elapsedSeconds > 0 ? Math.min(360, elapsedSeconds) : 360;

  const updateMinute = (minute: number, patch: Partial<MinuteFormState>) => {
    setMinutes((current) => current.map((entry) => (entry.minute === minute ? { ...entry, ...patch } : entry)));
  };

  const updatePauseReason = (index: number, reason: StepTestStopReason | null) => {
    setPauseIntervals((current) => current.map((entry, entryIndex) => (entryIndex === index ? { ...entry, reason } : entry)));
  };

  const updatePauseStart = (index: number, start: number) => {
    setPauseIntervals((current) => current.map((entry, entryIndex) => {
      if (entryIndex !== index || entry.end == null) return entry;
      const duration = Math.max(0, entry.end - entry.start);
      const safeStart = clampSeconds(start, Math.max(0, pauseTimelineLimit - duration));
      return { ...entry, start: safeStart, end: safeStart + duration };
    }));
  };

  const updatePauseDuration = (index: number, duration: number) => {
    setPauseIntervals((current) => current.map((entry, entryIndex) => {
      if (entryIndex !== index || entry.end == null) return entry;
      const safeDuration = clampSeconds(duration, Math.max(0, pauseTimelineLimit - entry.start));
      return { ...entry, end: entry.start + safeDuration };
    }));
  };

  const addPauseInterval = () => {
    const lastCompletedEnd = pauseIntervals.reduce(
      (latest, entry) => (entry.end == null ? latest : Math.max(latest, entry.end)),
      0,
    );
    const duration = pauseTimelineLimit > 0 ? 1 : 0;
    const start = Math.min(lastCompletedEnd, Math.max(0, pauseTimelineLimit - duration));
    setPauseIntervals((current) => [...current, { start, end: start + duration, reason: pauseReason }]);
  };

  const removePauseInterval = (index: number) => {
    setPauseIntervals((current) => current.filter((_, entryIndex) => entryIndex !== index));
  };

  const handleStart = () => {
    if (timerRunning || elapsedSeconds >= 360 || startedAt) return;
    if (!startedAt) setStartedAt(new Date().toISOString());
    timerStartedAtRef.current = Date.now();
    setTimerRunning(true);
  };

  const handlePause = () => {
    if (!timerRunning || activePause) return;
    const started = timerStartedAtRef.current;
    const nextElapsed = started == null ? accumulatedSecondsRef.current : accumulatedSecondsRef.current + Math.floor((Date.now() - started) / 1000);
    setElapsedSeconds(nextElapsed);
    setPauseIntervals((current) => [...current, { start: nextElapsed, reason: pauseReason }]);
  };

  const handleResume = () => {
    if (!timerRunning || !activePause || elapsedSeconds >= 360) return;
    setPauseIntervals((current) => {
      const next = [...current];
      const lastOpenIndex = next.findIndex((entry) => entry.end == null);
      if (lastOpenIndex >= 0) {
        next[lastOpenIndex] = { ...next[lastOpenIndex], end: elapsedSeconds };
      }
      return next;
    });
  };

  const handleResetTimer = () => {
    accumulatedSecondsRef.current = 0;
    timerStartedAtRef.current = null;
    setTimerRunning(false);
    setElapsedSeconds(0);
    setStartedAt(null);
    setEndedAt(null);
    setPauseIntervals([]);
  };

  const handleFinish = () => {
    if (totalSteps <= 0) {
      toast.error('Informe o número total de subidas para concluir o 6MST.');
      return;
    }
    const started = timerStartedAtRef.current;
    const nextElapsed =
      timerRunning && started != null ? accumulatedSecondsRef.current + Math.floor((Date.now() - started) / 1000) : elapsedSeconds;
    const safeElapsed = Math.min(nextElapsed, 360);
    const nextPauseIntervals = activePause
      ? pauseIntervals.map((entry) => (entry.end == null ? { ...entry, end: safeElapsed } : entry))
      : pauseIntervals;
    const nextPauseReport = toPauseReport(nextPauseIntervals);

    if (timerRunning) {
      accumulatedSecondsRef.current = safeElapsed;
      setElapsedSeconds(safeElapsed);
      timerStartedAtRef.current = null;
      setTimerRunning(false);
    }
    if (activePause) setPauseIntervals(nextPauseIntervals);
    const finishedAtIso = new Date().toISOString();
    setEndedAt(finishedAtIso);
    const finalPayload = buildSixMSTPayload({
      protocol: {
        stepHeightCm: parseNullableNumber(stepHeightCm) ?? 20,
      },
      baseline: toVitalsPayload(baseline),
      perMinute: minutes.map((entry) => ({
        minute: entry.minute,
        hr: entry.hr.trim() || null,
        spo2: entry.spo2.trim() || null,
        totalSteps: parseInteger(entry.totalSteps),
        notes: entry.notes.trim() || null,
      })),
      final: toVitalsPayload(finalBlock),
      totalSteps,
      stopReport: nextPauseReport,
      timer: {
        elapsed_s: safeElapsed,
        completed: safeElapsed >= 360,
        startedAt,
        endedAt: finishedAtIso,
      },
      notes,
      patientContext: effectivePatientContext,
      finishedAt: finishedAtIso,
    });
    finalPayload.report = {
      ...(finalPayload.report ?? {}),
      interpretation: resolveSixMSTInterpretation(finalPayload),
      referenceSource: SIX_MST_REFERENCE_METADATA.source,
    };
    onNext(finalPayload);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-[24px] border border-cyan-200 bg-cyan-50/80 p-5 text-cyan-950">
        <Info className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="space-y-2 text-sm">
          <div className="font-semibold">6-minute step test (6MST) em cadência livre</div>
          <div>
            Degrau padrão de 20 cm, 6 minutos, sem apoio de MMSS e com possibilidade de pausas mantendo o cronômetro correndo.
          </div>
          <div className="text-cyan-900/80">
            Referência normativa: {SIX_MST_REFERENCE_METADATA.source} Equação preditiva integrada ao resultado quando idade, sexo, altura e peso estiverem disponíveis.
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Tempo decorrido" value={formatClock(elapsedSeconds)} note={elapsedSeconds >= 360 ? 'Tempo-alvo atingido' : 'Cronômetro do protocolo'} />
        <MetricCard label="Subidas totais" value={String(totalSteps)} note={summary.severity} />
        <MetricCard
          label="Previsto"
          value={predictedSteps != null ? predictedSteps.toFixed(1) : '—'}
          note={summary.percentPredicted != null ? `${summary.percentPredicted.toFixed(1)}% do previsto` : 'Sem dados antropométricos suficientes'}
        />
        <MetricCard
          label="Faixa normativa"
          value={normativeBand ? normativeBand.label : '—'}
          note={normativeBand ? `Limite inferior (10º percentil): ${normativeBand.p10} degraus • Média da faixa: ${normativeBand.mean} degraus` : 'Idade/sexo ausentes'}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
        <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Execução</div>
              <div className="mt-1 text-lg font-bold text-slate-900">Contador e cronômetro do 6MST</div>
            </div>
            <div className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
              {elapsedSeconds >= 360 ? 'Concluído' : timerRunning ? 'Em andamento' : 'Pausado'}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr_auto]">
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Cronômetro</div>
              <div className="mt-2 text-5xl font-black tracking-tight text-slate-900">{formatClock(elapsedSeconds)}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  onClick={handleStart}
                  disabled={timerRunning || elapsedSeconds >= 360 || startedAt != null}
                >
                  <Play className="h-4 w-4" />
                  Iniciar
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  onClick={handlePause}
                  disabled={!timerRunning || !!activePause}
                >
                  <Pause className="h-4 w-4" />
                  Registrar pausa
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  onClick={handleResume}
                  disabled={!timerRunning || !activePause || elapsedSeconds >= 360}
                >
                  <Play className="h-4 w-4" />
                  Retomar
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                  onClick={handleResetTimer}
                >
                  <RotateCcw className="h-4 w-4" />
                  Resetar
                </button>
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Contador</div>
              <div className="mt-2 text-5xl font-black tracking-tight text-slate-900">{totalSteps}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700" onClick={() => setTotalSteps((current) => Math.max(0, current - 1))}>
                  <Minus className="h-4 w-4" />
                </button>
                <button type="button" className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700" onClick={() => setTotalSteps((current) => current + 1)}>
                  <Plus className="h-4 w-4" />
                </button>
                <button type="button" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700" onClick={() => setTotalSteps((current) => current + 5)}>
                  +5
                </button>
              </div>
              <label className="mt-3 block space-y-1">
                <div className="text-xs font-medium text-slate-600">Ajuste manual</div>
                <input
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={String(totalSteps)}
                  onChange={(event) => setTotalSteps(Math.max(0, parseInteger(event.target.value) ?? 0))}
                />
              </label>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-[180px_1fr]">
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">Altura do degrau (cm)</div>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={stepHeightCm}
                onChange={(event) => setStepHeightCm(event.target.value)}
              />
            </label>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              Referência: {SIX_MST_REFERENCE_METADATA.equationLabel}
              <div className="mt-1 text-xs text-slate-500">{SIX_MST_REFERENCE_METADATA.equationDescription}</div>
            </div>
          </div>
        </div>

        <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Leitura automática</div>
            <div className="mt-1 text-lg font-bold text-slate-900">{summary.severity}</div>
          </div>
          <div className="space-y-3 text-sm text-slate-700">
            <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
              <div className="font-semibold text-slate-900">Interpretação</div>
              <div className="mt-2 leading-relaxed">{resolveSixMSTInterpretation(payloadPreview)}</div>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
              <div>SpO2 mínima: <span className="font-semibold text-slate-900">{summary.minSpo2 != null ? `${summary.minSpo2.toFixed(1)}%` : '—'}</span></div>
              <div className="mt-1">Queda de SpO2: <span className="font-semibold text-slate-900">{summary.spo2Drop != null ? `${summary.spo2Drop.toFixed(1)}%` : '—'}</span></div>
              <div className="mt-1">FC pico: <span className="font-semibold text-slate-900">{summary.peakHr != null ? `${summary.peakHr.toFixed(0)} bpm` : '—'}</span></div>
              <div className="mt-1">Tempo parado: <span className="font-semibold text-slate-900">{pauseReport.total_stopped_fmt}</span></div>
            </div>
            {summary.belowNormativeLowerLimit ? (
              <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>Desempenho abaixo do limite inferior esperado (10º percentil) para a faixa normativa disponível.</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <VitalsEditor title="Repouso pré-teste" state={baseline} onChange={(patch) => setBaseline((current) => ({ ...current, ...patch }))} />
        <VitalsEditor title="Pós-teste / recuperação imediata" state={finalBlock} onChange={(patch) => setFinalBlock((current) => ({ ...current, ...patch }))} />
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Registro minuto a minuto</div>
            <div className="mt-1 text-lg font-bold text-slate-900">Anote FC, SpO2 e subidas acumuladas ao fim de cada minuto</div>
          </div>
        </div>
        <div className="mt-4 overflow-hidden rounded-[24px] border border-slate-100">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
                <th className="px-4 py-3">Min</th>
                <th className="px-4 py-3">FC</th>
                <th className="px-4 py-3">SpO2</th>
                <th className="px-4 py-3">Subidas acumuladas</th>
                <th className="px-4 py-3">Observações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {minutes.map((entry) => (
                <tr key={`6mst-minute-${entry.minute}`}>
                  <td className="px-4 py-3 font-bold text-slate-900">{entry.minute}</td>
                  <td className="px-4 py-3">
                    <input className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entry.hr} onChange={(event) => updateMinute(entry.minute, { hr: event.target.value })} />
                  </td>
                  <td className="px-4 py-3">
                    <input className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entry.spo2} onChange={(event) => updateMinute(entry.minute, { spo2: event.target.value })} />
                  </td>
                  <td className="px-4 py-3">
                    <input className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entry.totalSteps} onChange={(event) => updateMinute(entry.minute, { totalSteps: event.target.value })} />
                  </td>
                  <td className="px-4 py-3">
                    <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entry.notes} onChange={(event) => updateMinute(entry.minute, { notes: event.target.value })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Pausas / interrupções</div>
            <div className="mt-1 text-lg font-bold text-slate-900">
              {isEditing ? 'Revise o motivo, o tempo e a quantidade de pausas' : 'Registre pausas mantendo o cronômetro correndo'}
            </div>
          </div>
          <div className={`grid gap-2 ${isEditing ? 'md:grid-cols-[220px_auto_auto]' : 'md:grid-cols-[220px_auto]'}`}>
            <select
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={pauseReason}
              aria-label="Motivo da próxima pausa"
              onChange={(event) => setPauseReason(event.target.value as StepTestStopReason)}
            >
              {PAUSE_REASON_OPTIONS.map((option) => (
                <option key={`pause-reason-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isEditing ? (
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={addPauseInterval}
                disabled={timerRunning || !!activePause}
              >
                <Plus className="h-4 w-4" />
                Adicionar pausa
              </button>
            ) : null}
            <div className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700">
              {activePause ? 'Pausa em aberto' : `${pauseReport.intervals.length} pausa(s) registrada(s)`}
            </div>
          </div>
        </div>

        {pauseReport.intervals.length ? (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {isEditing
              ? pauseIntervals.map((entry, index) => {
                  if (entry.end == null) return null;
                  const duration = Math.max(0, entry.end - entry.start);
                  return (
                    <div key={`pause-entry-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-slate-900">Pausa {index + 1}</div>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => removePauseInterval(index)}
                          disabled={timerRunning}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Remover
                        </button>
                      </div>

                      <label className="mt-3 block space-y-1">
                        <div className="text-xs font-medium text-slate-600">Motivo</div>
                        <select
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:bg-slate-100"
                          value={entry.reason ?? ''}
                          disabled={timerRunning}
                          onChange={(event) => updatePauseReason(index, event.target.value ? event.target.value as StepTestStopReason : null)}
                        >
                          <option value="">Motivo não informado</option>
                          {PAUSE_REASON_OPTIONS.map((option) => (
                            <option key={`pause-${index}-reason-${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <div className="mt-3 grid gap-3 xl:grid-cols-2">
                        <ClockEditor
                          id={`pause-${index}-start`}
                          label="Início da pausa"
                          value={entry.start}
                          maxSeconds={Math.max(0, pauseTimelineLimit - duration)}
                          disabled={timerRunning}
                          onChange={(value) => updatePauseStart(index, value)}
                        />
                        <ClockEditor
                          id={`pause-${index}-duration`}
                          label="Tempo parado"
                          value={duration}
                          maxSeconds={Math.max(0, pauseTimelineLimit - entry.start)}
                          disabled={timerRunning}
                          onChange={(value) => updatePauseDuration(index, value)}
                        />
                      </div>

                      <div className="mt-3 text-xs text-slate-500">
                        Intervalo: {formatClock(entry.start)} → {formatClock(entry.end)} ({formatClock(duration)})
                      </div>
                    </div>
                  );
                })
              : pauseReport.intervals.map((entry, index) => (
                  <div key={`pause-entry-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <div className="font-semibold text-slate-900">{entry.reason_label || 'Motivo não informado'}</div>
                    <div className="mt-1">{entry.from_fmt} → {entry.to_fmt} ({entry.duration_fmt})</div>
                  </div>
                ))}
          </div>
        ) : (
          <div className="mt-4 text-sm text-slate-500">Nenhuma pausa concluída foi registrada até o momento.</div>
        )}
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Observações</div>
        <textarea
          className="mt-3 min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Descreva intercorrências, padrão de execução, necessidade de incentivo ou qualquer detalhe relevante."
        />
      </div>

      <div className="flex flex-wrap justify-end gap-3">
        <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className="rounded-xl bg-cyan-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm" onClick={handleFinish}>
          Salvar 6MST
        </button>
      </div>
    </div>
  );
}
